/**
 * Paper Trading Options Toolbar Select Options Helpers.
 *
 * Formats option expiries, strikes, and combobox option models for toolbar dropdowns.
 */

export interface ExpirySelectOption {
  value: string;
  text: string;
}

export interface StrikeOptionModel {
  strike: number;
  strikeText: string;
  selected: boolean;
}

/**
 * Transforms an array of raw expiry strings into dropdown option descriptors.
 */
export function buildExpirySelectOptions(
  expiryList: string[],
): ExpirySelectOption[] {
  if (!Array.isArray(expiryList)) return [];
  return expiryList.map(exp => ({
    value: String(exp),
    text: String(exp),
  }));
}

/**
 * Returns the strike price at the given offset index relative to the ATM strike.
 */
export function strikeAtOffset(
  strikes: Array<{ strike: number }>,
  atmStrike: number | null | undefined,
  offset: number = 0,
): number | null | undefined {
  if (!strikes || !strikes.length || atmStrike == null) return atmStrike;
  const atmIdx = strikes.findIndex(s => s.strike === atmStrike);
  if (atmIdx === -1) return atmStrike;
  const idx = Math.max(0, Math.min(strikes.length - 1, atmIdx + offset));
  return strikes[idx].strike;
}

/**
 * Resolves auto-selected Call and Put strikes based on current inputs, ATM strike, and user offsets.
 */
export function resolveAutoSelectedStrikes(
  strikes: Array<{ strike: number }>,
  atmStrike: number | null | undefined,
  ceOffset: number = 0,
  peOffset: number = 0,
  currentCallVal?: string | null,
  currentPutVal?: string | null,
): { callStrike?: number; putStrike?: number } {
  const result: { callStrike?: number; putStrike?: number } = {};
  if (!currentCallVal && atmStrike != null) {
    const s = strikeAtOffset(strikes, atmStrike, ceOffset);
    if (s != null) result.callStrike = s;
  }
  if (!currentPutVal && atmStrike != null) {
    const s = strikeAtOffset(strikes, atmStrike, peOffset);
    if (s != null) result.putStrike = s;
  }
  return result;
}

/**
 * Builds strike combobox option models with selection tagging.
 */
export function buildStrikeOptionModels(
  strikes: number[],
  selectedValue?: number | string | null,
): StrikeOptionModel[] {
  if (!Array.isArray(strikes)) return [];
  const selectedNum =
    selectedValue != null && selectedValue !== "" ? Number(selectedValue) : null;
  return strikes.map(s => ({
    strike: s,
    strikeText: String(s),
    selected: selectedNum != null && Number(s) === selectedNum,
  }));
}
